import React from 'react';
import { Alert, Box, Stack } from '@mui/material';
import {
  useCampaignMutations,
  useReportFigureConfig,
  useReportSectionConfig,
} from '../../../hooks/campaign-hooks';
import { CampaignReportFigure, CampaignReportSection } from '../../../support/campaigns';
import { GRID_COLS } from '../../../support/reportView/layout';
import type { SectionLayout } from '../../../support/reportView/types';
import ReportGrid, { type ReportGridItem } from '../../Assessments/report/ReportGrid';
import { BuilderRoot, GhostButton, SectionHeader, StatusPill, builderStyles } from './builder/primitives';

/**
 * Management > Campaigns > Report Builder > Layout.
 *
 * The visual section-grid canvas — the campaign report's half of the report-grid unification. Each
 * report section is a cell the admin drags and resizes on the SAME 12-column grid the assessment
 * report uses (`ReportGrid` + `GRID_COLS` from `support/reportView`), and the geometry is stored on
 * the section row as `layout_x/y/w/h`.
 *
 * What the geometry drives (see `guided/modules/report/grid.ts`): consecutive sections the admin
 * places on the same grid row (equal `layout_y`) render side-by-side in the SE editor, the PDF and
 * this canvas; a section's `layout_w` is its column span. The reading *sequence* is `order`, which we
 * keep in sync with the grid here — every drag/resize re-linearizes the sections by `(y, x)` and PUTs
 * the new `order` alongside the new geometry. The SE can still reorder/hide/add on top of this
 * ("SE arrangement on top"); this canvas only seeds the campaign's default arrangement.
 *
 * Figures are NOT placed here — they ride their section's prose flow via `anchor` (authored on the
 * figure Canvas). This cell shows which figures sit in each section so the admin sees the shape of
 * the section, but their position is the section's, not a free cell.
 *
 * Writes are full-record PUTs (Go runtime). Controls degrade to read-only when `canWrite` is false.
 */

// A section with no authored geometry stacks full-width at a sensible default height; the grid's
// vertical compaction then packs the seeded rows. Matches the pre-grid single-column report exactly.
const DEFAULT_SECTION_W = GRID_COLS;
const DEFAULT_SECTION_H = 4;

function hasLayout(row: CampaignReportSection): boolean {
  return (
    row.layout_x != null && row.layout_y != null && row.layout_w != null && row.layout_h != null
  );
}

/**
 * The grid geometry for each section: its stored `{x,y,w,h}` where authored, otherwise a full-width
 * cell stacked below whatever precedes it — so an un-authored campaign seeds a clean single column.
 */
function seedLayouts(rows: CampaignReportSection[]): Map<number, SectionLayout> {
  const byId = new Map<number, SectionLayout>();
  let cursorY = 0;
  for (const row of rows) {
    if (hasLayout(row)) {
      const layout = { x: row.layout_x!, y: row.layout_y!, w: row.layout_w!, h: row.layout_h! };
      byId.set(row.id, layout);
      cursorY = Math.max(cursorY, layout.y + layout.h);
    } else {
      byId.set(row.id, { x: 0, y: cursorY, w: DEFAULT_SECTION_W, h: DEFAULT_SECTION_H });
      cursorY += DEFAULT_SECTION_H;
    }
  }
  return byId;
}

export function SectionGridCanvas(props: {
  campaignId: number;
  campaignName: string;
  canWrite: boolean;
  /** Back to the section list. Omitted when embedded (the parent Layout view owns navigation). */
  onBack?: () => void;
  /**
   * Rendered inside the combined Layout view (below its header, above the figure editor) rather than
   * as a standalone page: drops the `BuilderRoot` wrapper (the parent supplies one) and the back
   * button, and shows a compact sub-heading instead of the full page header.
   */
  embedded?: boolean;
}): JSX.Element {
  const { campaignId, campaignName, canWrite, onBack, embedded } = props;
  const { data: sectionRows, isLoading } = useReportSectionConfig(campaignId);
  const { data: figureRows } = useReportFigureConfig(campaignId);
  const { update, isSaving, error } = useCampaignMutations('reportSection');

  const rows = React.useMemo(
    () => ((sectionRows ?? []) as CampaignReportSection[]).slice().sort((a, b) => a.order - b.order),
    [sectionRows],
  );

  // Figures grouped by their section id, so each cell can show what sits in it (read-only here).
  const figuresBySection = React.useMemo(() => {
    const map = new Map<string, CampaignReportFigure[]>();
    for (const fig of (figureRows ?? []) as CampaignReportFigure[]) {
      const list = map.get(fig.section_id) ?? [];
      list.push(fig);
      map.set(fig.section_id, list);
    }
    return map;
  }, [figureRows]);

  const seeded = React.useMemo(() => seedLayouts(rows), [rows]);

  // A drag/resize settled. Re-linearize the sections by (y, x) for reading order and PUT every row
  // whose geometry OR order changed — one full-record write per changed row (Go 400s on a delta).
  const handleLayoutChange = React.useCallback(
    (byId: Record<string, SectionLayout>) => {
      if (!canWrite) return;
      const layoutOf = (row: CampaignReportSection): SectionLayout =>
        byId[String(row.id)] ?? seeded.get(row.id) ?? { x: 0, y: 0, w: DEFAULT_SECTION_W, h: DEFAULT_SECTION_H };

      const ordered = rows
        .slice()
        .sort((a, b) => {
          const la = layoutOf(a);
          const lb = layoutOf(b);
          return la.y - lb.y || la.x - lb.x;
        });

      ordered.forEach((row, index) => {
        const layout = layoutOf(row);
        const geomChanged =
          row.layout_x !== layout.x ||
          row.layout_y !== layout.y ||
          row.layout_w !== layout.w ||
          row.layout_h !== layout.h;
        const orderChanged = row.order !== index;
        if (geomChanged || orderChanged) {
          update.mutate({
            ...row,
            order: index,
            layout_x: layout.x,
            layout_y: layout.y,
            layout_w: layout.w,
            layout_h: layout.h,
          });
        }
      });
    },
    [canWrite, rows, seeded, update],
  );

  const items: ReportGridItem[] = rows.map((row) => {
    const figs = figuresBySection.get(row.section_id) ?? [];
    return {
      id: String(row.id),
      layout: seeded.get(row.id) ?? { x: 0, y: 0, w: DEFAULT_SECTION_W, h: DEFAULT_SECTION_H },
      minW: 2,
      minH: 2,
      content: (
        <div
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: '1px solid var(--cb-border, #d1d5db)',
            borderRadius: 8,
            background: 'var(--cb-surface, #fff)',
            boxShadow: '0 1px 3px rgba(15,23,42,0.08)',
            color: 'var(--cb-text, #111827)',
          }}
        >
          <div
            className="report-cell-drag-handle"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              borderBottom: '1px solid var(--cb-border, #d1d5db)',
              background: 'var(--cb-surface-2, #eef1f5)',
              cursor: canWrite ? 'move' : 'default',
            }}
          >
            <span style={{ fontSize: 13, color: '#9ca3af' }} aria-hidden>
              ⠿
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flex: 1,
              }}
            >
              {row.number}. {row.title || row.section_id || 'Untitled section'}
            </span>
          </div>
          <div style={{ flex: 1, padding: '8px 10px', overflow: 'auto', fontSize: 12 }}>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {row.is_data_section && <StatusPill tone="progress">Data</StatusPill>}
              {row.ai_question != null && <StatusPill tone="done">AI</StatusPill>}
              {row.hidden_by_default && <StatusPill tone="idle">Hidden</StatusPill>}
              <StatusPill tone="idle">{row.source}</StatusPill>
            </Stack>
            {figs.length > 0 ? (
              <>
                <div style={{ color: '#6b7280', marginBottom: 4 }}>
                  {figs.length} figure{figs.length === 1 ? '' : 's'}
                </div>
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  {figs.map((fig) => (
                    <span
                      key={fig.id}
                      style={{
                        fontSize: 11,
                        padding: '2px 6px',
                        borderRadius: 6,
                        background: 'var(--cb-surface-2, #eef1f5)',
                        border: '1px solid var(--cb-border, #d1d5db)',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {fig.kind === 'chart' ? '▦ ' : '▣ '}
                      {fig.title || fig.figure_id}
                    </span>
                  ))}
                </Stack>
              </>
            ) : (
              <div style={{ color: '#9ca3af' }}>No figures</div>
            )}
          </div>
        </div>
      ),
    };
  });

  const body = (
    <>
      {error != null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error)?.message ?? 'Save failed.'}
        </Alert>
      )}

      {isLoading && <p className={builderStyles.lead}>Loading sections…</p>}
      {!isLoading && rows.length === 0 && (
        <p className={builderStyles.lead}>
          This campaign has no report sections yet — add them on the Sections tab, then arrange them
          here.
        </p>
      )}

      {rows.length > 0 && (
        <div
          style={{
            background: 'var(--cb-surface-2, #eef1f5)',
            border: '1px solid var(--cb-border, #d1d5db)',
            borderRadius: 12,
            padding: 16,
          }}
        >
          <ReportGrid
            items={items}
            interactive={canWrite && !isSaving}
            draggableCancel=".report-cell-action, .report-cell-action *"
            onLayoutChange={handleLayoutChange}
          />
        </div>
      )}
    </>
  );

  if (embedded) {
    return (
      <Box component="section">
        <SectionHeader
          compact
          title="Section layout"
          subtitle="Drag and resize the report's sections on the grid. Sections on the same row render side-by-side; a section's width is its column span. Reading order follows the grid; the SE can still reorder on top."
        />
        {body}
      </Box>
    );
  }

  return (
    <BuilderRoot>
      <SectionHeader
        title={`Report Layout — ${campaignName}`}
        subtitle="Drag and resize the report's sections on the grid. Sections you place on the same row render side-by-side; a section's width is its column span. Reading order follows the grid; the SE can still reorder on top."
        actions={
          <Stack direction="row" spacing={1}>
            <GhostButton onClick={onBack}>← Sections</GhostButton>
          </Stack>
        }
      />
      {body}
    </BuilderRoot>
  );
}

export default SectionGridCanvas;
